import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import { useCallback, useContext, useEffect, useState } from 'react'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import {
  useAutocompleteTitleOptionsLazyQuery,
  useTitlesCreateOptionsQuery,
} from '@kistill/client/data-access'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'
import { DatePicker } from '@mui/x-date-pickers'
import { format, isValid } from 'date-fns'
import DateFnsProvider from '../../../providers/DateFnsProvider'

interface ItemTitleFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function ItemTitleFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: ItemTitleFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('itemTitle', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } =
    useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)

  const [inputValueTitle, setInputValueTitle] = useState('')
  const [titleOptions, setTitleOptions] = useState<SelectItem[]>([])
  const { data } = useTitlesCreateOptionsQuery()

  const languageOptions = data?.languageOptions as SelectItem[]

  const [getTitleOptions] = useAutocompleteTitleOptionsLazyQuery({
    variables: { query: inputValueTitle },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.titleOptions) {
        setTitleOptions(data.titleOptions.slice()) // Use slice() to create a new array
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'itemTitle',
  })

  const filterOptions = createFilterOptions<SelectItem>({
    trim: true,
  })

  const handleInputChangeTitle = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueTitle(value)
  }

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
    }
  }

  useEffect(() => {
    getTitleOptions() // Trigger the query to fetch title options
  }, [getTitleOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'itemTitle',
    columnVisibility,
    setValue,
    clearSpecificFields,
  )

  return (
    <DateFnsProvider>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={1}>
          {!hiddenFields.has('titleSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='titleSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(titleOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, titleSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    value={value || []}
                    inputValue={inputValueTitle}
                    onInputChange={handleInputChangeTitle}
                    clearOnBlur={false}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Titill' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('languagesSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='languagesSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(languageOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, languagesSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    value={value || []}
                    filterOptions={filterOptions}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Tungumál' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('premiereLocation') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='premiereLocation'
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label='Frumsýningarstaður'
                    size='small'
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('comments') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='comments'
                render={({ field }) => (
                  <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label='Athugasemdir'
                      fullWidth
                      size='small'
                    />
                  </Tooltip>
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('year') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='year'
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label='Útgáfuár'
                    views={['year']}
                    {...register('year')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={false}
                        fullWidth
                        size='small'
                      />
                    )}
                    value={value ? new Date(value) : null}
                    onChange={(date) => {
                      if (date && isValid(date)) {
                        const yearString = date.getFullYear().toString()
                        if (yearString.length === 4) {
                          onChange(yearString)
                        } else {
                          onChange(null)
                        }
                      } else {
                        onChange(null)
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('premiereDate') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='premiereDate'
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label='Dagsetning frumsýningar'
                    inputFormat='dd.MM.yyyy'
                    views={['year', 'month', 'day']}
                    {...register('premiereDate')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={false}
                        fullWidth
                        size='small'
                      />
                    )}
                    value={value ? new Date(value) : null}
                    onChange={(date) => {
                      if (date && isValid(date)) {
                        const iso = format(date, "yyyy-MM-dd'T'00:00:00")
                        onChange(iso)
                      } else {
                        onChange(null)
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        <FilterControls
          selected={selected}
          clearFilters={clearFilters}
          register={register}
          deselectCheckboxChange={deselectCheckboxChange}
        />
      </form>
    </DateFnsProvider>
  )
}
